import * as React from "react";
import { Film, Play, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import {
  CONTENT_TYPE_LABEL,
  formatContentDate,
  formatTimecode,
  parseTimecode,
} from "../schedulerUtils";
import type { PlacementResult, Program } from "../types";
import { BARE_CONTROL, Field, MediaThumb } from "./parts";

interface Props {
  program: Program | null;
  onTrim: (id: string, inSec: number, outSec: number) => PlacementResult;
  onSetTitle: (id: string, text: string) => PlacementResult;
  onSetDescription: (id: string, text: string) => PlacementResult;
  onRemove: (id: string) => void;
  onPreview: () => void;
  onSave: () => void;
  onError: (message: string) => void;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm text-foreground" style={{ marginBottom: 6 }}>
      {children}
    </div>
  );
}

function TrimField({
  label,
  valueSec,
  resetSec,
  disabled,
  onCommit,
}: {
  label: string;
  valueSec: number;
  resetSec: number;
  disabled?: boolean;
  onCommit: (text: string) => void;
}) {
  const [draft, setDraft] = React.useState(formatTimecode(valueSec));
  React.useEffect(() => setDraft(formatTimecode(valueSec)), [valueSec]);
  return (
    <div>
      <Label>{label}</Label>
      <Field>
        <Input
          aria-label={label}
          disabled={disabled}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            onCommit(draft);
            setDraft(formatTimecode(valueSec));
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className={BARE_CONTROL}
        />
        <button
          type="button"
          aria-label={`Reset ${label}`}
          disabled={disabled}
          className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
          style={{ marginRight: 12 }}
          onClick={() => onCommit(formatTimecode(resetSec))}
        >
          <RotateCcw size={16} />
        </button>
      </Field>
    </div>
  );
}

/** Admin-editable program title (defaults to the game's matchup); commits
 * on blur, staged until Save like the description. A blank draft snaps
 * back: the guide and the backend both require a title. */
function TitleField({
  program,
  onCommit,
}: {
  program: Program;
  onCommit: (text: string) => void;
}) {
  const [draft, setDraft] = React.useState(program.title);
  React.useEffect(() => setDraft(program.title), [program.id, program.title]);
  return (
    <div style={{ marginTop: 20 }}>
      <Label>Title</Label>
      <Field>
        <Input
          aria-label="Title"
          value={draft}
          placeholder="Program title shown in the guide"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft.trim() === "") {
              setDraft(program.title);
              return;
            }
            if (draft.trim() !== program.title) onCommit(draft);
          }}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
          className={BARE_CONTROL}
        />
      </Field>
    </div>
  );
}

/** Admin-authored copy for the guide's details panel; commits on blur. The
 * border lives on the wrapping Field because v2's scoped reset zeroes
 * border-width on the textarea element itself. */
function DescriptionField({
  program,
  onCommit,
}: {
  program: Program;
  onCommit: (text: string) => void;
}) {
  const [draft, setDraft] = React.useState(program.description ?? "");
  React.useEffect(() => setDraft(program.description ?? ""), [program.id, program.description]);
  return (
    <div style={{ marginTop: 20 }}>
      <Label>Description</Label>
      {/* items-stretch and an auto height: BARE_CONTROL's h-9 is a
          single-line input height and Field centers its children, which
          clipped a multi-line textarea top and bottom. */}
      <Field className="items-stretch">
        <textarea
          aria-label="Description"
          value={draft}
          rows={3}
          placeholder="Shown under the program title in the guide"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if ((program.description ?? "") !== draft) onCommit(draft);
          }}
          className="sched-bare h-auto w-full resize-none border-0 bg-transparent text-sm shadow-none ring-0 focus-visible:ring-0"
          style={{ padding: "10px 12px", lineHeight: 1.5 }}
        />
      </Field>
    </div>
  );
}

export function EditProgramPanel({
  program,
  onTrim,
  onSetTitle,
  onSetDescription,
  onRemove,
  onPreview,
  onSave,
  onError,
}: Props) {
  const report = (result: PlacementResult) => {
    if (!result.ok && result.error) onError(result.error);
  };

  const commitTrim = (which: "in" | "out", text: string) => {
    if (!program) return;
    const sec = parseTimecode(text);
    if (sec === null) {
      onError("Use the format h:mm:ss for trim points.");
      return;
    }
    const next =
      which === "in" ? [sec, program.trimOutSec] : [program.trimInSec, sec];
    if (next[0] === program.trimInSec && next[1] === program.trimOutSec) return;
    report(onTrim(program.id, next[0], next[1]));
  };

  const disabled = !program;

  return (
    <section
      className="rounded-lg border border-border bg-card"
      style={{ padding: 20 }}
      aria-label="Edit Program"
    >
      <div className="flex items-center justify-between" style={{ gap: 12 }}>
        <h2 className="font-bold text-foreground" style={{ fontSize: 20, lineHeight: 1.3 }}>
          Edit Program
        </h2>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => program && onRemove(program.id)}
          className="bg-card text-destructive ring-1 ring-destructive/40 hover:bg-destructive/10"
        >
          <Trash2 size={16} />
          Remove
        </Button>
      </div>

      {program ? (
        <>
          <div className="flex items-center" style={{ gap: 16, marginTop: 16 }}>
            <MediaThumb
              thumbnail={program.thumbnail}
              logos={program.logos}
              className="shrink-0 rounded-md"
              style={{ width: 128, aspectRatio: "16 / 9" }}
            />
            <div>
              <div className="text-base font-semibold leading-tight text-foreground">
                {program.title}
              </div>
              <div className="text-sm text-muted-foreground" style={{ marginTop: 6 }}>
                {/* The clip's ORIGINAL runtime (stored in the program's
                    metadata, exact since the drop-time probe), not the
                    trimmed airing span: the admin trimming needs the full
                    length as the reference. "~" marks the rare block whose
                    runtime is still the estimate (probe miss, legacy row). */}
                {CONTENT_TYPE_LABEL[program.type]} <span aria-hidden>|</span>{" "}
                {program.durationEstimated ? "~" : ""}
                {formatTimecode(program.durationSec)} <span aria-hidden>|</span>{" "}
                {formatContentDate(program.contentDate)}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 16,
              marginTop: 20,
            }}
          >
            <TrimField
              label="Trim In Point"
              valueSec={program.trimInSec}
              resetSec={0}
              onCommit={(t) => commitTrim("in", t)}
            />
            <TrimField
              label="Trim Out Point"
              valueSec={program.trimOutSec}
              resetSec={program.durationSec}
              onCommit={(t) => commitTrim("out", t)}
            />
          </div>

          <TitleField
            program={program}
            onCommit={(text) => report(onSetTitle(program.id, text))}
          />

          <DescriptionField
            program={program}
            onCommit={(text) => report(onSetDescription(program.id, text))}
          />

          <button
            type="button"
            onClick={onPreview}
            className="flex items-center text-sm font-medium text-action-hover hover:underline"
            style={{ gap: 8, marginTop: 20 }}
          >
            <span
              className="flex items-center justify-center rounded-full bg-action text-white"
              style={{ width: 24, height: 24 }}
            >
              <Play size={12} fill="currentColor" />
            </span>
            Preview and set trim points
          </button>

          {/* Changes autosave after a pause; this flushes them right now.
              A click blurs the description field first, so an in-progress
              draft commits before the flush runs. */}
          <Button
            type="button"
            onClick={onSave}
            className="w-full bg-action text-white hover:bg-action-hover"
            style={{ height: 36, marginTop: 20 }}
          >
            Save
          </Button>
        </>
      ) : (
        <div
          className="flex flex-col items-center text-center"
          style={{ padding: "40px 16px" }}
        >
          <div
            className="flex items-center justify-center rounded-full bg-muted"
            style={{ width: 56, height: 56 }}
          >
            <Film size={22} className="text-muted-foreground" />
          </div>
          <div className="font-semibold text-foreground" style={{ marginTop: 16, fontSize: 15 }}>
            No program selected
          </div>
          <p className="text-sm text-muted-foreground" style={{ marginTop: 4, maxWidth: 260 }}>
            Select a program on the timeline to edit its trim points.
          </p>
        </div>
      )}
    </section>
  );
}
